"use client";

import { FC, HTMLAttributes, useEffect, useRef, useState } from "react";
import { useIntersection } from "react-use";

import { ComponentSharedVideo, ResponseConnectedResource, ResponseImage } from "@Shared/api";
import { useScreens } from "@Shared/hooks";
import { Image, ImageProps } from "@Shared/ui";
import { getImageSrcFromApiData, twcx } from "@Shared/utils";

export type MediaProps = {
    className?: string;
    classNameDevices?: string;
    image?: ResponseConnectedResource<ResponseImage>;
    imageDevices?: ResponseConnectedResource<ResponseImage>;
    imageProps?: Omit<ImageProps, "className" | "src">;
    video?: ComponentSharedVideo;
    videoDevices?: ComponentSharedVideo;
    videoProps?: Omit<HTMLAttributes<HTMLVideoElement>, "className" | "src">;
};

export const Media: FC<MediaProps> = ({
    className,
    classNameDevices,
    image,
    imageDevices,
    imageProps,
    video,
    videoDevices,
    videoProps,
}) => {
    const refVideo = useRef<HTMLVideoElement>(null);

    const [src, setSrc] = useState<string>("");
    const [poster, setPoster] = useState<string | undefined>();

    const intersection = useIntersection(refVideo, { threshold: 0 });
    const { isLg } = useScreens();

    useEffect(() => {
        if (!video?.file.data && !videoDevices?.file.data) {
            return;
        }

        const videoSrc = getImageSrcFromApiData(video?.file.data?.attributes);
        const videoDevicesSrc = getImageSrcFromApiData(videoDevices?.file.data?.attributes);

        if ((isLg || !videoDevices) && videoSrc && src !== videoSrc) {
            setSrc(videoSrc);
            setPoster(getImageSrcFromApiData(video?.poster.data?.attributes));
        } else if (!isLg && videoDevicesSrc && src !== videoDevicesSrc) {
            setSrc(videoDevicesSrc);
            setPoster(getImageSrcFromApiData(videoDevices?.poster.data?.attributes));
        }
    }, [isLg]);

    useEffect(() => {
        if (refVideo.current && src) {
            refVideo.current.load();
        }
    }, [src]);

    useEffect(() => {
        if (intersection && intersection.isIntersecting && refVideo.current?.paused) {
            refVideo.current?.play();
        } else if (intersection && !intersection.isIntersecting && !refVideo.current?.paused) {
            refVideo.current?.pause();
        }
    }, [src, intersection]);

    if (!image?.data && !video?.file.data) {
        return;
    }

    return (
        <>
            {src && (
                <video
                    ref={refVideo}
                    className={twcx(
                        {
                            "max-lg:hidden": !videoDevices?.file && imageDevices?.data,
                            "lg:hidden": !video?.file && videoDevices?.file && image?.data,
                        },
                        className
                    )}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    poster={poster}
                    {...videoProps}
                >
                    <source src={src} />
                </video>
            )}

            {!video?.file && image?.data && (
                <Image
                    className={twcx(
                        {
                            "max-lg:hidden": videoDevices?.file || imageDevices?.data,
                        },
                        className
                    )}
                    src={getImageSrcFromApiData(image.data.attributes)}
                    alt="Image"
                    width={image.data.attributes.width}
                    height={image.data.attributes.height}
                    role="presentation"
                    sizes={{ default: "96vw", lg: "99vw" }}
                    {...imageProps}
                />
            )}

            {!videoDevices?.file && imageDevices?.data && (
                <Image
                    className={twcx("lg:hidden", className, classNameDevices)}
                    src={getImageSrcFromApiData(imageDevices.data.attributes)}
                    alt="Image"
                    width={imageDevices.data.attributes.width}
                    height={imageDevices.data.attributes.height}
                    role="presentation"
                    sizes={{ default: "96vw", lg: "99vw" }}
                    {...imageProps}
                />
            )}
        </>
    );
};
